import { MenuItem, SxProps, TextField, Theme } from "@mui/material";
import { AnchoredMenu } from "../../common/dashboard/AnchoredMenu";
import { matchesDropdownSearch } from "../../common/dashboard/dropdownSearchMatch";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  InvoiceDesignCheckbox,
  invoiceDesignFontFamily,
} from "./InvoiceDesignSystem";

export type InvoiceSearchableMultiSelectOption = Readonly<{
  checked: boolean;
  id: string;
  label: string;
  onSelect: () => void;
}>;

type InvoiceSearchableMultiSelectMenuProps = Readonly<{
  anchorEl: HTMLElement | null;
  ariaLabel: string;
  checkboxGap?: number;
  menuSx?: SxProps<Theme>;
  noOptionsSx?: SxProps<Theme>;
  noOptionsText?: string;
  onClose: () => void;
  // Design SearchableSelect select-all (CMP Dashboard (21) ds-components
  // SearchableSelect.jsx:310-323): one row that ticks, or clears when all are
  // ticked, every option the search shows.
  onSelectAll?: (visibleOptionIds: string[], allVisibleChecked: boolean) => void;
  options: ReadonlyArray<InvoiceSearchableMultiSelectOption>;
  optionRowHeight?: number;
  searchable?: boolean;
  searchPlaceholder: string;
  testIdPrefix: string;
}>;

const InvoiceSearchableMultiSelectMenu = ({
  anchorEl,
  ariaLabel,
  checkboxGap = 8,
  menuSx = {},
  noOptionsSx = {},
  noOptionsText = "No matching options",
  onClose,
  onSelectAll,
  options,
  optionRowHeight = 41,
  searchable = true,
  searchPlaceholder,
  testIdPrefix,
}: InvoiceSearchableMultiSelectMenuProps) => {
  const [search, setSearch] = useState("");
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const anchorWidth = anchorEl?.getBoundingClientRect().width || 0;

  useEffect(() => {
    setSearch("");
    setActiveOptionId(null);
  }, [anchorEl]);

  const visibleOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return searchable && normalizedSearch
      ? options.filter((option) =>
          matchesDropdownSearch(option.label, normalizedSearch),
        )
      : options;
  }, [options, search, searchable]);
  const activeOptionIndex = visibleOptions.findIndex(
    (option) => option.id === activeOptionId,
  );
  const menuId = `${testIdPrefix}-menu`;
  const activeDescendantId =
    activeOptionIndex >= 0
      ? `${testIdPrefix}-option-${activeOptionIndex}`
      : undefined;

  useEffect(() => {
    if (activeDescendantId) {
      document
        .getElementById(activeDescendantId)
        ?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeDescendantId]);

  const focusMenuControl = () => {
    (searchable ? searchInputRef.current : optionsRef.current)?.focus({
      preventScroll: true,
    });
  };
  const handleSearchInputRef = useCallback(
    (input: HTMLInputElement | null) => {
      searchInputRef.current = input;
      if (input && anchorEl) {
        input.focus({ preventScroll: true });
      }
    },
    [anchorEl],
  );

  const handleClose = () => {
    onClose();
    anchorEl?.focus({ preventScroll: true });
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      handleClose();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();

      if (visibleOptions.length === 0) {
        setActiveOptionId(null);
        return;
      }

      const nextIndex =
        event.key === "ArrowDown"
          ? activeOptionIndex < 0
            ? 0
            : (activeOptionIndex + 1) % visibleOptions.length
          : activeOptionIndex < 0
            ? visibleOptions.length - 1
            : (activeOptionIndex - 1 + visibleOptions.length) %
              visibleOptions.length;

      setActiveOptionId(visibleOptions[nextIndex].id);
      return;
    }

    if (
      (event.key === "Enter" ||
        event.key === " " ||
        event.key === "Spacebar") &&
      activeOptionIndex >= 0
    ) {
      event.preventDefault();
      event.stopPropagation();
      visibleOptions[activeOptionIndex].onSelect();
      focusMenuControl();
    }
  };

  return (
    <AnchoredMenu
      anchorEl={anchorEl}
      autoFocusItem={false}
      placement="bottom-start"
      MenuListProps={{
        "aria-label": searchable ? ariaLabel : undefined,
        id: searchable ? menuId : undefined,
        role: searchable ? "menu" : "presentation",
        sx: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "min(355px, calc(100vh - 16px))",
          overflow: "hidden",
          py: 0,
        },
      }}
      onClose={handleClose}
      open={Boolean(anchorEl)}
      PaperProps={{
        "data-testid": `${testIdPrefix}-paper`,
        style: {
          boxSizing: "border-box",
          maxWidth: "calc(100vw - 16px)",
          width: anchorWidth > 0 ? anchorWidth : undefined,
        },
        sx: [
          {
            border: "1px solid #9E9E9E",
            borderRadius: "8px",
            boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
            maxHeight: "min(355px, calc(100vh - 16px))",
            mt: "4px",
            overflow: "hidden",
          },
          ...(Array.isArray(menuSx) ? menuSx : [menuSx]),
        ],
      }}
    >
      {searchable ? (
        <div
          data-testid={`${testIdPrefix}-search-row`}
          onKeyDown={(event) => {
            if (event.key !== "Escape") {
              event.stopPropagation();
            }
          }}
          style={{
            borderBottom: "1px solid #9E9E9E",
            flexShrink: 0,
            padding: 8,
          }}
        >
          <TextField
            fullWidth
            inputRef={handleSearchInputRef}
            inputProps={{
              "aria-activedescendant": activeDescendantId,
              "aria-controls": menuId,
              "aria-expanded": Boolean(anchorEl),
              "aria-haspopup": "menu",
              "aria-label": searchPlaceholder,
            }}
            onChange={({ target }) => {
              setSearch(target.value);
              setActiveOptionId(null);
            }}
            onKeyDown={handleOptionKeyDown}
            placeholder={searchPlaceholder}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: invoiceDesignFontFamily,
                fontSize: 14,
                height: "38px !important",
                lineHeight: "20px",
                minHeight: "38px !important",
                "& fieldset": { borderColor: "#9E9E9E" },
                "&.Mui-focused fieldset": {
                  borderColor: "#9E9E9E",
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                boxSizing: "border-box",
                height: "100%",
                lineHeight: "20px",
                padding: "8px 12px",
              },
            }}
            value={search}
          />
        </div>
      ) : null}

      <div
        aria-activedescendant={!searchable ? activeDescendantId : undefined}
        aria-label={!searchable ? ariaLabel : undefined}
        data-testid={`${testIdPrefix}-options-scroll`}
        id={!searchable ? menuId : undefined}
        onKeyDown={!searchable ? handleOptionKeyDown : undefined}
        ref={(element) => {
          optionsRef.current = element;
          if (element && anchorEl && !searchable) {
            element.focus({ preventScroll: true });
          }
        }}
        role={!searchable ? "menu" : undefined}
        style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        tabIndex={-1}
      >
        {onSelectAll && visibleOptions.length > 0 ? (() => {
          const allVisibleChecked = visibleOptions.every((option) => option.checked);
          return (
            <MenuItem
              data-testid={`${testIdPrefix}-select-all`}
              onClick={() => {
                onSelectAll(visibleOptions.map((option) => option.id), allVisibleChecked);
                focusMenuControl();
              }}
              style={{
                borderBottom: "1px solid #9E9E9E",
                boxSizing: "border-box",
                fontFamily: invoiceDesignFontFamily,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: `${Math.min(20, optionRowHeight - 20)}px`,
                minHeight: optionRowHeight,
                padding: "10px 12px",
              }}
              sx={{ "&:hover": { backgroundColor: "#F7F7F8" } }}
            >
              <InvoiceDesignCheckbox
                checked={allVisibleChecked}
                inputProps={{ "aria-label": "Select All" }}
                tabIndex={-1}
                sx={{ mr: `${checkboxGap}px` }}
              />
              Select All
            </MenuItem>
          );
        })() : null}
        {visibleOptions.map((option, index) => {
          const active = index === activeOptionIndex;

          return (
            <MenuItem
              aria-current={active ? "true" : undefined}
              data-active={active ? "true" : undefined}
              id={`${testIdPrefix}-option-${index}`}
              key={option.id}
              onClick={() => {
                option.onSelect();
                focusMenuControl();
              }}
              onMouseMove={() => setActiveOptionId(option.id)}
              selected={option.checked}
              style={{
                boxSizing: "border-box",
                fontFamily: invoiceDesignFontFamily,
                fontSize: 14,
                fontWeight: option.checked ? 600 : 400,
                lineHeight: `${Math.min(20, optionRowHeight - 20)}px`,
                minHeight: optionRowHeight,
                padding: "10px 12px",
              }}
              sx={{
                backgroundColor: active ? "#F7F7F8" : "transparent",
                "&.Mui-focusVisible, &:hover": {
                  backgroundColor: "#F7F7F8",
                },
                "&.Mui-selected": {
                  backgroundColor: active ? "#F7F7F8" : "transparent",
                  "&:hover": { backgroundColor: "#F7F7F8" },
                },
              }}
            >
              <InvoiceDesignCheckbox
                checked={option.checked}
                inputProps={{ "aria-label": option.label }}
                tabIndex={-1}
                sx={{ mr: `${checkboxGap}px` }}
              />
              {option.label}
            </MenuItem>
          );
        })}

        {visibleOptions.length === 0 ? (
          <MenuItem
            disabled
            sx={[
              {
                boxSizing: "border-box",
                fontFamily: invoiceDesignFontFamily,
                fontSize: 14,
                lineHeight: `${Math.min(20, optionRowHeight - 20)}px`,
                minHeight: optionRowHeight,
                px: "12px",
                py: "10px",
              },
              ...(Array.isArray(noOptionsSx) ? noOptionsSx : [noOptionsSx]),
            ]}
          >
            {noOptionsText}
          </MenuItem>
        ) : null}
      </div>
    </AnchoredMenu>
  );
};

export default InvoiceSearchableMultiSelectMenu;
